"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
const Error_1 = __importDefault(require("@mui/icons-material/Error"));
const Lock_1 = __importDefault(require("@mui/icons-material/Lock"));
const Visibility_1 = __importDefault(require("@mui/icons-material/Visibility"));
const VisibilityOff_1 = __importDefault(require("@mui/icons-material/VisibilityOff"));
const material_1 = require("@mui/material");
const lexico_1 = __importDefault(require("@tradeshift/lexico"));
const axios_1 = require("axios");
const react_1 = require("react");
const react_router_1 = require("react-router");
const react_use_1 = require("react-use");
const zod_1 = require("zod");
const strings_1 = require("../../../../constants/strings");
const uiConstants_1 = require("../../../../constants/uiConstants");
const sessionQuery_1 = require("../../../../queries/sessionQuery");
const sessionRepository_1 = require("../../../../repositories/sessionRepository");
const Root_1 = require("../../../../router/Root");
const routerUtils_1 = require("../../../../router/routerUtils");
const zustandStore_1 = require("../../../../zustandStore");
const pageSpinner_1 = __importDefault(require("../../../molecules/pageSpinner"));
const activationForm_1 = require("../activateForm/activationForm");
const banner_1 = require("../common/banner");
const title_1 = require("../common/title");
const passwordSetupForm_1 = require("../hsbc/steps/defaultRegistration/step_3/passwordSetupForm");
const authForm_1 = __importDefault(require("../loginform/authForm"));
const usePasswordInput_1 = require("../loginform/hooks/usePasswordInput");
const lexico = new lexico_1.default();
const resetPasswordSchema = zod_1.z
    .object({
    password: zod_1.z.string().check(ctx => {
        if (ctx.value.length < 10) {
            ctx.issues.push({
                code: 'custom',
                message: passwordSetupForm_1.passwordRequireMessage,
                input: ctx.value,
            });
        }
        else if (!activationForm_1.passwordRegex.test(ctx.value)) {
            ctx.issues.push({
                code: 'custom',
                message: passwordSetupForm_1.passwordRequireMessage,
                input: ctx.value,
            });
        }
    }),
    confirmPassword: zod_1.z.string(),
})
    .refine(data => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
});
const ResetPassword = () => {
    const theme = (0, material_1.useTheme)();
    const [isFormLoading, setIsFormLoading] = (0, react_1.useState)(false);
    const [showPassword, setShowPassword] = (0, react_1.useState)(false);
    const [showConfirmPassword, setShowConfirmPassword] = (0, react_1.useState)(false);
    const { userId, activationToken } = (0, react_router_1.useParams)();
    const { state: passwordState, handlePasswordChange, handlePasswordValidation, resetPasswordFormState, } = (0, usePasswordInput_1.usePasswordInput)();
    const navigate = (0, react_router_1.useNavigate)();
    const { setNotification } = (0, zustandStore_1.useZustand)();
    const isFormValid = resetPasswordSchema.safeParse({
        password: passwordState.passwordValue,
        confirmPassword: passwordState.confirmPasswordValue,
    }).success;
    const resetPassword = (0, sessionQuery_1.useResetPasswordQuery)();
    const { value: userData, error, loading, } = (0, react_use_1.useAsync)(async () => {
        const response = await sessionRepository_1.SessionRepository.confirmToken({
            userId: userId || '',
            activationToken: activationToken || '',
        });
        Root_1.queryClient.setQueryData(sessionQuery_1.sessionKeys.getActivateUser(userId || '', activationToken || ''), response);
        if (globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP && response?.passwordAttemptsExceeded) {
            navigateToLockedAccountMessage('password');
            return null;
        }
        return response;
    }, [userId, activationToken]);
    const { navigateToSigninMessage, navigateToLockedAccountMessage } = (0, routerUtils_1.useSigninMessageNavigation)();
    if (loading) {
        return (0, jsx_runtime_1.jsx)(pageSpinner_1.default, { isLoading: true });
    }
    if (error) {
        void navigateToSigninMessage({
            heading: globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP
                ? strings_1.errorStrings.DEFAULT
                : lexico.tr('Something went wrong'),
            message: lexico.trc('Error message', 'The link to activate your account is invalid or has expired.'),
        });
        return;
    }
    const handleSubmit = (event) => {
        event.preventDefault();
        setIsFormLoading(true);
        const options = {
            activationToken: activationToken || '',
            password: passwordState.passwordValue,
            userId: userId || '',
        };
        resetPassword.mutate(options, {
            onSuccess: async () => {
                void navigate('/signin/message', {
                    state: {
                        heading: lexico.trc('Component title', 'Success!'),
                        message: 'Your password has been reset. Please login.',
                    },
                });
                setIsFormLoading(false);
            },
            onError: error => {
                setIsFormLoading(false);
                if (globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP &&
                    (0, axios_1.isAxiosError)(error) &&
                    error.response?.status === 403 &&
                    error.response?.data?.errorCode === 'LOCKED_ACCOUNT') {
                    return navigateToLockedAccountMessage('password');
                }
                setNotification([
                    {
                        type: strings_1.notificationStrings.WARNING,
                        message: globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP
                            ? strings_1.errorStrings.DEFAULT
                            : lexico.trc('Warning message', 'Oops! Something went wrong. Please try again!'),
                    },
                ]);
            },
            onSettled: () => {
                resetPasswordFormState();
            },
        });
    };
    return ((0, jsx_runtime_1.jsx)(authForm_1.default, { children: (0, jsx_runtime_1.jsxs)(material_1.Container, { style: {
                width: '360px',
                padding: 0,
                border: `1px solid ${theme.palette.tsGoColor.grayLightest}`,
                backgroundColor: theme.palette.tsGoColor.white,
                marginTop: theme?.tsGoUnit.tsUnitHalf,
            }, children: [(0, jsx_runtime_1.jsx)(banner_1.Banner, { className: "banner signin" }), (0, jsx_runtime_1.jsx)(title_1.Title, { children: lexico.trc('Component title', 'Reset Password') }), (0, jsx_runtime_1.jsxs)("form", { "data-testid": "form", style: {
                        padding: `0 ${theme.tsGoUnit.tsUnit} ${theme.tsGoUnit.tsUnit}`,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: theme.tsGoUnit.tsUnit,
                    }, autoComplete: "off", onSubmit: handleSubmit, children: [(0, jsx_runtime_1.jsx)(material_1.FormControl, { style: { padding: 0 }, fullWidth: true, variant: "outlined", children: (0, jsx_runtime_1.jsx)(material_1.TextField, { label: lexico.tr('Username'), style: { padding: 0, margin: 0 }, type: "email", fullWidth: true, disabled: true, margin: "normal", variant: "outlined", autoComplete: "off", value: userData?.username || '', slotProps: {
                                    input: {
                                        endAdornment: ((0, jsx_runtime_1.jsx)(material_1.InputAdornment, { position: "end", children: (0, jsx_runtime_1.jsx)(Lock_1.default, { color: "disabled" }) })),
                                    },
                                    htmlInput: {
                                        'data-testid': 'username',
                                    },
                                    inputLabel: {
                                        style: { fontWeight: theme.typography.tsFontWeight.default },
                                    },
                                } }) }), (0, jsx_runtime_1.jsxs)(material_1.FormControl, { style: { padding: 0 }, fullWidth: true, variant: "outlined", error: passwordState.isPasswordError, children: [(0, jsx_runtime_1.jsx)(material_1.TextField, { name: "password", label: "Password", style: { padding: 0, margin: 0 }, type: showPassword ? 'text' : 'password', required: true, fullWidth: true, margin: "normal", variant: "outlined", autoComplete: "off", onChange: handlePasswordChange, onBlur: handlePasswordValidation, value: passwordState.passwordValue, error: passwordState.isPasswordError, slotProps: {
                                        htmlInput: {
                                            'data-testid': 'password',
                                        },
                                        inputLabel: {
                                            style: { fontWeight: theme.typography.tsFontWeight.default },
                                        },
                                        input: {
                                            endAdornment: passwordState.passwordValue?.length > 0 ? ((0, jsx_runtime_1.jsx)(material_1.InputAdornment, { position: "end", children: (0, jsx_runtime_1.jsx)(material_1.IconButton, { onClick: () => setShowPassword(prev => !prev), edge: "end", children: showPassword ? (0, jsx_runtime_1.jsx)(Visibility_1.default, { fontSize: "small" }) : (0, jsx_runtime_1.jsx)(VisibilityOff_1.default, { fontSize: "small" }) }) })) : null,
                                        },
                                    } }), passwordState.isPasswordError && ((0, jsx_runtime_1.jsxs)(material_1.FormHelperText, { style: { marginLeft: 0 }, children: [(0, jsx_runtime_1.jsx)(Error_1.default, { fontSize: "small", style: { verticalAlign: 'middle' } }), " ", passwordSetupForm_1.passwordRequireMessage] }))] }), (0, jsx_runtime_1.jsxs)(material_1.FormControl, { style: { padding: 0 }, fullWidth: true, variant: "outlined", error: passwordState.isConfirmPasswordError, children: [(0, jsx_runtime_1.jsx)(material_1.TextField, { name: "confirm password", label: strings_1.sessionStrings.CONFIRM_PASSWORD, style: { padding: 0, margin: 0 }, type: showConfirmPassword ? 'text' : 'password', required: true, fullWidth: true, margin: "normal", variant: "outlined", autoComplete: "off", onChange: handlePasswordChange, onBlur: handlePasswordValidation, value: passwordState.confirmPasswordValue, error: passwordState.isConfirmPasswordError, slotProps: {
                                        htmlInput: {
                                            'data-testid': 'confirm-password',
                                        },
                                        inputLabel: {
                                            style: { fontWeight: theme.typography.tsFontWeight.default },
                                        },
                                        input: {
                                            endAdornment: passwordState.confirmPasswordValue?.length > 0 ? ((0, jsx_runtime_1.jsx)(material_1.InputAdornment, { position: "end", children: (0, jsx_runtime_1.jsx)(material_1.IconButton, { onClick: () => setShowConfirmPassword(prev => !prev), edge: "end", children: showConfirmPassword ? (0, jsx_runtime_1.jsx)(Visibility_1.default, { fontSize: "small" }) : (0, jsx_runtime_1.jsx)(VisibilityOff_1.default, { fontSize: "small" }) }) })) : null,
                                        },
                                    } }), passwordState.isConfirmPasswordError && ((0, jsx_runtime_1.jsxs)(material_1.FormHelperText, { style: { marginLeft: 0 }, children: [(0, jsx_runtime_1.jsx)(Error_1.default, { fontSize: "small", style: { verticalAlign: 'middle' } }), ' ', passwordState.confirmPasswordValue === passwordState.passwordValue
                                            ? passwordSetupForm_1.passwordRequireMessage
                                            : lexico.tr('The passwords did not match.')] }))] }), (0, jsx_runtime_1.jsx)(material_1.Button, { style: {
                                width: '100%',
                                fontSize: theme.typography.pxToRem(theme.typography.tsFontSize.default),
                            }, "data-testid": "submit", color: uiConstants_1.UI_BUTTON_PRIMARY, disabled: !isFormValid, type: "submit", loading: isFormLoading, loadingPosition: "start", startIcon: (0, jsx_runtime_1.jsx)(jsx_runtime_1.Fragment, {}), variant: "contained", children: lexico.trc('Form input label', 'Set New Password') })] })] }) }));
};
exports.default = ResetPassword;
